import { GraduationCap, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Skill } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { useDeactivateSkill } from "@/features/skills/hooks/useSkills";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface Props {
  skill: Skill;
  onEdit: () => void;
}

export function SkillCard({ skill, onEdit }: Props) {
  const { t } = useTranslation("skills");
  const { t: tc } = useTranslation("common");
  const deactivate = useDeactivateSkill();
  const canManage = useCan(Action.SKILL_UPDATED, {
    owner_team_id: skill.owner_team_id,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleDeactivate() {
    try {
      await deactivate.mutateAsync(skill.id);
      toast.success(t("card.deactivated"));
      setConfirmOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="flex flex-col rounded-md border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-mono text-base font-semibold">{skill.name}</h2>
        </div>
        {!skill.is_active ? (
          <Badge variant="destructive">{t("card.inactive")}</Badge>
        ) : null}
      </div>
      <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
        {skill.description}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {canManage ? (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Pencil className="h-3 w-3" />
            {tc("actions.edit")}
          </Button>
        ) : null}
        {canManage && skill.is_active ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-danger-700 hover:text-danger-700"
            onClick={() => setConfirmOpen(true)}
            disabled={deactivate.isPending}
          >
            <Trash2 className="h-3 w-3" />
            {t("card.deactivate")}
          </Button>
        ) : null}
      </div>

      <ConfirmDestructiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("card.deactivateDialog.title")}
        description={t("card.deactivateDialog.description")}
        resourceName={skill.name}
        resourceKind={t("card.deactivateDialog.kind")}
        confirmLabel={t("card.deactivateDialog.confirm")}
        onConfirm={handleDeactivate}
        isPending={deactivate.isPending}
      />
    </li>
  );
}
